/**
 * Frame Selection Validation Utilities
 * 
 * Centralized validation logic for frame jump distance enforcement
 * Used across all auto-selection code paths (pan, pan-end, fallback)
 */

import { NavigationSettings } from '@/types/NavigationSettings'
import type { OffsetMap } from '@/types/offsetMap'

export interface JumpDistanceCheckResult {
  allowed: boolean
  reason?: string
  distance: number
  fromFrame: number
  toFrame: number
  usedExtendedPairs?: boolean
}

/**
 * Validates if a frame jump is allowed based on navigation settings
 * 
 * Enforces max jump distance for phase correlation (sequential mode) when:
 * 1. enforceMaxJumpDistance setting is enabled
 * 2. reconstructionMethod is 'phase_correlation'
 * 
 * However, if extended_connectivity exists, large jumps are allowed if:
 * - A path exists through extended pairs
 * 
 * @param fromFrameIndex - Current frame index
 * @param toFrameIndex - Target frame index
 * @param reconstructionMethod - Dataset reconstruction method ('colmap' | 'phase_correlation')
 * @param navigationSettings - Current navigation settings
 * @param offsetMap - Optional offset map with extended_connectivity
 * @returns Validation result with allowed flag, reason, and distance
 */
export function validateFrameJump(
  fromFrameIndex: number,
  toFrameIndex: number,
  reconstructionMethod: 'colmap' | 'phase_correlation' | undefined,
  navigationSettings: NavigationSettings | null,
  offsetMap?: OffsetMap | null
): JumpDistanceCheckResult {
  const actualDistance = Math.abs(toFrameIndex - fromFrameIndex)

  // Jump distance enforcement only applies when:
  // 1. Setting is enabled
  // 2. Reconstruction method is phase_correlation
  const shouldEnforce = 
    navigationSettings?.enforceMaxJumpDistance === true && 
    reconstructionMethod === 'phase_correlation'

  // 🔍 DEBUG: Log the state
  console.log(`[validateFrameJump] Frame ${fromFrameIndex}→${toFrameIndex}, distance: ${actualDistance}`, {
    enforceMaxJumpDistance: navigationSettings?.enforceMaxJumpDistance,
    reconstructionMethod,
    shouldEnforce,
    nearWindow: navigationSettings?.nearWindow,
    hasOffsetMap: !!offsetMap,
    hasExtendedConnectivity: !!offsetMap?.extended_connectivity,
  })

  if (!shouldEnforce) {
    console.log(`  ✅ Enforcement disabled - ALLOW`)
    return {
      allowed: true,
      distance: actualDistance,
      fromFrame: fromFrameIndex,
      toFrame: toFrameIndex
    }
  }

  const maxJumpDistance = navigationSettings?.nearWindow ?? 10

  // ✅ NEW: Allow ANY frame within nearWindow, regardless of connectivity
  if (actualDistance <= maxJumpDistance) {
    console.log(`  ✅ Distance ${actualDistance} <= max ${maxJumpDistance} - ALLOW (within nearWindow)`)
    return {
      allowed: true,
      distance: actualDistance,
      fromFrame: fromFrameIndex,
      toFrame: toFrameIndex
    }
  }

  // Only check extended_connectivity for jumps BEYOND nearWindow
  // (if you want to allow longer jumps via extended pairs, implement here)
  const extendedConnectivity = offsetMap?.extended_connectivity
  if (extendedConnectivity) {
    const fromKey = String(fromFrameIndex)
    const connectedFrames = extendedConnectivity[fromKey] || []
    
    if (connectedFrames.includes(toFrameIndex)) {
      console.log(`  ✅ Connected via extended pairs (beyond nearWindow) - ALLOW`)
      return {
        allowed: true,
        distance: actualDistance,
        fromFrame: fromFrameIndex,
        toFrame: toFrameIndex,
        usedExtendedPairs: true
      }
    }
  }

  console.log(`  ❌ Distance ${actualDistance} > max ${maxJumpDistance} and not connected - BLOCK`)
  return {
    allowed: false,
    reason: `Sequential mode: Jump blocked (Frame ${fromFrameIndex} → ${toFrameIndex}, distance: ${actualDistance}, max: ±${maxJumpDistance})`,
    distance: actualDistance,
    fromFrame: fromFrameIndex,
    toFrame: toFrameIndex
  }
}

/**
 * Logs the validation result with context
 * 
 * @param result - Validation result from validateFrameJump
 * @param context - Description of where validation occurred (e.g., "Primary auto-selection")
 * @param verbose - If false, only logs blocked jumps (default: true)
 */
export function logJumpValidation(
  result: JumpDistanceCheckResult,
  context: string,
  verbose: boolean = true
): void {
  if (!result.allowed) {
    console.warn(`🚫 ${context}: ${result.reason}`)
  } else if (verbose && result.distance > 0) {
    console.log(
      `✅ ${context}: Jump allowed ` +
      `(Frame ${result.fromFrame} → ${result.toFrame}, distance: ${result.distance})`
    )
  }
}
